import { ApiServiceResponse, ServiceType } from '../../types/services';
import { ApiError, ApiErrorCode, createApiError } from '../../types/errors';
import { callAdminApi } from './wixAdminApi';

export class ServiceTypesApi {
  private serviceTypeProvider: () => Promise<Array<{
    type: ServiceType;
    imageUrl: string;
    serviceId: string;
    defaultDuration?: number;
  }>>;

  constructor(
    serviceTypeProvider?: () => Promise<Array<{ type: ServiceType; imageUrl: string; serviceId: string; defaultDuration?: number }>>
  ) {
    // Default provider now calls Wix Bookings services via the admin API
    this.serviceTypeProvider = serviceTypeProvider || this.fetchWixBookingsServiceTypes;
    // Reference to avoid TS6133 unused error
    void this.resolveWixImageUrl;
  }

  async getServiceTypes(): Promise<Array<{ type: ServiceType; imageUrl: string; serviceId: string; defaultDuration?: number }>> {
    try {
      return await this.serviceTypeProvider();
    } catch (error: unknown) {
      if (typeof error === 'object' && error !== null && 'code' in error && 'message' in error) {
        return [];
      }

      const apiError: ApiError = createApiError(
        ApiErrorCode.UNKNOWN_ERROR,
        error instanceof Error ? error.message : 'Error fetching service types'
      );

      throw apiError;
    }
  }

  async queryWixBookingsServices(params: Record<string, unknown> = {}): Promise<Array<{ type: ServiceType; imageUrl: string }>> {
    try {
      const response = await callAdminApi('queryServices', 'services', params);

      const SERVICE_TYPE_IMAGES: Record<string, string> = {
        "Window Cleaning": "/assets/images/window-cleaning.jpg",
        "Free Estimate": "/assets/images/free-estimate.jpg",
        "Refrigerator Cleaning": "/assets/images/fridge-cleaning.jpg",
        "Commercial Cleaning": "/assets/images/commercial-cleaning.jpg",
        "Post Construction Cleaning": "/assets/images/post-construction.jpg",
        "Deep Cleaning": "/assets/images/deep-cleaning.jpg",
        "Recurring Cleaning": "/assets/images/recurring-cleaning.jpg",
        "Oven Cleaning": "/assets/images/oven-cleaning.jpg",
        "Move Out Cleaning": "/assets/images/move-out.jpg",
        "Move-Out Cleaning": "/assets/images/move-out.jpg",
        "AirBnb Cleaning Service": "/assets/images/airbnb-cleaning.jpg"
      };

      if (response && Array.isArray(response.items)) {
        return response.items.map((svc: unknown) => {
          const service = svc as { type?: string; title?: string; name?: string };
          const type = service.type || service.title || service.name || 'Unknown Service';
          const imageUrl = SERVICE_TYPE_IMAGES[type] || '/assets/images/bucket.svg';
          return {
            type: type as ServiceType,
            imageUrl
          };
        });
      }
      return [];
    } catch (error) {
      console.error('Error fetching Wix Bookings services:', error);
      return [];
    }
  }

  async getWixBookingsService(serviceId: string): Promise<{ type: ServiceType; imageUrl: string } | null> {
    try {
      const response = await callAdminApi('getService', 'services', { serviceId });
      
      if (response) {
        const SERVICE_TYPE_IMAGES: Record<string, string> = {
          "Window Cleaning": "/assets/images/window-cleaning.jpg",
          "Free Estimate": "/assets/images/free-estimate.jpg",
          "Refrigerator Cleaning": "/assets/images/fridge-cleaning.jpg",
          "Commercial Cleaning": "/assets/images/commercial-cleaning.jpg",
          "Post Construction Cleaning": "/assets/images/post-construction.jpg",
          "Deep Cleaning": "/assets/images/deep-cleaning.jpg",
          "Recurring Cleaning": "/assets/images/recurring-cleaning.jpg",
          "Oven Cleaning": "/assets/images/oven-cleaning.jpg",
          "Move Out Cleaning": "/assets/images/move-out.jpg",
          "Move-Out Cleaning": "/assets/images/move-out.jpg",
          "AirBnb Cleaning Service": "/assets/images/airbnb-cleaning.jpg"
        };

       const type = (response as ApiServiceResponse).type || (response as ApiServiceResponse).title || (response as ApiServiceResponse).name || 'Unknown Service';
        const imageUrl = SERVICE_TYPE_IMAGES[type] || '/assets/images/bucket.svg';
        
        return {
          type: type as ServiceType,
          imageUrl
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching Wix Bookings service:', error);
      return null;
    }
  }

  private async resolveWixImageUrl(wixUrl: string): Promise<string> {
    try {
      const match = wixUrl.match(/^wix:image:\/\/v1\/([^\/#]+)/);
      if (match) {
        const baseUrl = `https://static.wixstatic.com/media/${match[1]}`;
        
        // Try different extensions
        const extensions = ['.jpg', '.png', '.webp'];
        for (const ext of extensions) {
          const testUrl = baseUrl + ext;
          try {
            const response = await fetch(testUrl, { method: 'HEAD' });
            if (response.ok) {
              return testUrl;
            }
          } catch {
            // Continue to next extension
          }
        }
        
        // Default to .jpg if none work
        return baseUrl + '.jpg';
      }
      
      return wixUrl;
    } catch {
      return '/assets/images/bucket.svg';
    }
  }

  // Add this method to preload images into browser cache
  private async preloadImage(url: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      const timeout = setTimeout(() => {
        resolve(false);
      }, 5000); // 5 second timeout for preloading
      
      img.onload = () => {
        clearTimeout(timeout);
        resolve(true);
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        resolve(false);
      };
      
      // This actually loads the image into browser cache
      img.src = url;
    });
  }

  private async fetchWixBookingsServiceTypes(): Promise<Array<{ type: ServiceType; imageUrl: string; serviceId: string }>> {
    try {
      const response = await callAdminApi('queryServices', 'services', {});

      const SERVICE_TYPE_IMAGES: Record<string, string> = {
        "Window Cleaning": "/assets/images/window-cleaning.jpg",
        "Free Estimate": "/assets/images/free-estimate.jpg",
        "Refrigerator Cleaning": "/assets/images/fridge-cleaning.jpg",
        "Commercial Cleaning": "/assets/images/commercial-cleaning.jpg",
        "Post Construction Cleaning": "/assets/images/post-construction.jpg",
        "Deep Cleaning": "/assets/images/deep-cleaning.jpg",
        "Recurring Cleaning": "/assets/images/recurring-cleaning.jpg",
        "Oven Cleaning": "/assets/images/oven-cleaning.jpg",
        "Move Out Cleaning": "/assets/images/move-out.jpg",
        "Move-Out Cleaning": "/assets/images/move-out.jpg",
        "AirBnb Cleaning Service": "/assets/images/airbnb-cleaning.jpg"
      };

      if (response && Array.isArray(response._items)) {
        // ✅ BATCH: Extract all service data first
        const serviceData = response._items.map((item: unknown, idx: number) => {
          const service = item as Record<string, unknown>;
          const rawName = (service.name as string) || (service.title as string) || (service.type as string) || `Service ${idx + 1}`;
          // Normalise Wix variant names to match ServiceType enum
          const serviceType = rawName === 'Move-Out Cleaning' ? 'Move Out Cleaning' : rawName;
          const serviceId = (service._id as string) || (service.id as string) || '';
          // Extract default duration (minutes) from Wix schedule or duration field
          const schedule = service.schedule as Record<string, unknown> | undefined;
          const availConstraints = schedule?.availabilityConstraints as Record<string, unknown> | undefined;
          const sessionDurations = availConstraints?.sessionDurations as number[] | undefined;
          const durationField = service.duration as Record<string, unknown> | undefined;
          const defaultDuration: number | undefined =
            (sessionDurations && sessionDurations.length > 0 ? sessionDurations[0] : undefined) ??
            (durationField?.value != null ? Number(durationField.value) : undefined) ??
            undefined;
          let wixImageUrl = '';
          const media = service.media as Record<string, unknown> | undefined;
          
          if (media?.mainMedia && typeof media.mainMedia === 'object' && media.mainMedia !== null) {
            const mainMedia = media.mainMedia as Record<string, unknown>;
            if (typeof mainMedia.image === 'string') {
              wixImageUrl = this.convertWixImageUrl(mainMedia.image);
            }
          }
          
          return {
            serviceType,
            wixImageUrl,
            fallbackUrl: SERVICE_TYPE_IMAGES[serviceType] || '/assets/images/bucket.svg',
            serviceId,
            defaultDuration
          };
        });

        // ✅ BATCH: Test all Wix images in parallel BEFORE returning
        
        const imageValidationPromises = serviceData.map(async (data) => {
          if (!data.wixImageUrl) {
            return { ...data, finalImageUrl: data.fallbackUrl };
          }
          
          try {
            // Test if Wix image is accessible
            const isValid = await this.testImageUrl(data.wixImageUrl);
            return {
              ...data,
              finalImageUrl: isValid ? data.wixImageUrl : data.fallbackUrl
            };
          } catch {
            return { ...data, finalImageUrl: data.fallbackUrl };
          }
        });

        // ✅ Wait for ALL image validations to complete
        const validatedServices = await Promise.all(imageValidationPromises);

        // ✅ NEW: Preload all final images into browser cache
        const preloadPromises = validatedServices.map(async (service) => {
          const success = await this.preloadImage(service.finalImageUrl);
          return success;
        });

        // Wait for all images to be preloaded
        await Promise.all(preloadPromises);

        // ✅ Return fully resolved service types with preloaded images and serviceId
        return validatedServices.map((service) => ({
          type: service.serviceType as ServiceType,
          imageUrl: service.finalImageUrl,
          serviceId: service.serviceId,
          defaultDuration: service.defaultDuration
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching Wix Bookings services:', error);
      return [];
    }
  }

  private convertWixImageUrl(wixUrl: string): string {
    if (!wixUrl || !wixUrl.startsWith('wix:image://')) {
      return wixUrl;
    }
    // Extract the static file name after wix:image://v1/ and before the next /, ignoring query/hash
    const match = wixUrl.match(/^wix:image:\/\/v1\/([^\/?#]+)/);
    let staticFileName = match ? match[1] : '';
    if (!staticFileName) {
      // Try to extract from other possible patterns
      const fallbackMatch = wixUrl.match(/^wix:image:\/\/v1\/([^?#]+)/);
      staticFileName = fallbackMatch ? fallbackMatch[1] : '';
    }
    // Remove any trailing query/hash from the file name
    staticFileName = staticFileName.split('?')[0].split('#')[0];
    // If it already ends with an image extension, return as is
    if (/\.(jpg|jpeg|png|gif|webp)$/i.test(staticFileName)) {
      return `https://static.wixstatic.com/media/${staticFileName}`;
    }
    // Otherwise, append .jpg
    return `https://static.wixstatic.com/media/${staticFileName}.jpg`;
  }

  // Helper method to test if an image URL is accessible
  private async testImageUrl(url: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      const timeout = setTimeout(() => {
        resolve(false);
      }, 3000); // 3 second timeout
      
      img.onload = () => {
        clearTimeout(timeout);
        resolve(true);
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        resolve(false);
      };
      
      img.src = url;
    });
  }
}